import React, { useEffect } from "react";
import { X, Microscope, ExternalLink, FlaskConical } from "lucide-react";

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-charcoal-100/60 last:border-b-0">
    <span className="text-[12.5px] tracking-wide text-charcoal-400">{label}</span>
    <span className="text-sm font-display font-bold text-charcoal-700 text-right">{value}</span>
  </div>
);

const ProfilePanel = ({ profile, isComponent = false }) => (
  <div className={`rounded-2xl border border-charcoal-100/60 ${isComponent ? "bg-cream-100" : "bg-cream-100"} p-5 md:p-6`}>
    <div className="flex items-center gap-2 mb-3">
      <Microscope className="w-3.5 h-3.5 text-sage-600" strokeWidth={2} />
      <span className="font-display text-[10.5px] tracking-[0.22em] font-bold uppercase text-charcoal-600">
        {isComponent ? "Component Profile" : "Molecular Profile"}
      </span>
    </div>
    <h3 className="font-display text-2xl md:text-[1.65rem] font-bold leading-tight text-charcoal-700 mb-5">
      What is {profile.name}?
    </h3>

    <div className="rounded-xl bg-white/70 border border-charcoal-100/40 px-4 md:px-5 py-2">
      <Row label="Type" value={profile.type} />
      <Row label="Amino Acids" value={profile.aminoAcids} />
      <Row label="Molecular Formula" value={profile.formula} />
      <Row label="Molecular Weight" value={profile.weight} />
    </div>

    <p className="mt-5 text-[13.5px] leading-relaxed text-charcoal-500">
      <span className="font-display font-bold text-charcoal-700">Mechanism:</span>{" "}
      {profile.mechanism}
    </p>

    {profile.pubchem ? (
      <a
        href={profile.pubchem}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`pubchem-link-${profile.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-sage-700 hover:text-sage-800 underline-offset-4 hover:underline transition-colors"
      >
        <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.2} />
        View on PubChem
      </a>
    ) : (
      <p className="mt-5 text-[11.5px] text-charcoal-400 italic">
        Not catalogued on PubChem (laboratory reagent).
      </p>
    )}
  </div>
);

/**
 * Premium "Compound Information" modal — opens on tap from Research Library cards.
 * Renders one or many ProfilePanels (GLOW is a triple blend).
 */
export const CompoundProfileModal = ({ open, onClose, profile }) => {
  // Close on Esc + lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !profile) return null;

  const isBlend = !!profile.isBlend;

  return (
    <div
      data-testid="compound-profile-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Compound Information"
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 animate-fade-in"
    >
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close compound information"
        onClick={onClose}
        data-testid="compound-modal-scrim"
        className="absolute inset-0 bg-charcoal-700/45 backdrop-blur-[2px]"
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-cream-100 rounded-[1.75rem] shadow-[0_40px_80px_-20px_rgba(60,40,55,0.45)] border border-white/70 animate-fade-up">
        {/* Sticky header */}
        <header className="sticky top-0 z-10 bg-cream-100/95 backdrop-blur-md border-b border-charcoal-100/50 px-6 md:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-2xl bg-sage-100 text-sage-700 flex items-center justify-center shrink-0">
              <FlaskConical className="w-4 h-4" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <h2 className="font-display text-xl md:text-2xl font-bold leading-tight text-charcoal-700 truncate">
                Compound Information
              </h2>
              <p className="text-[11.5px] text-charcoal-400 tracking-wide">
                Technical specifications · Research Use Only
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="compound-modal-close"
            aria-label="Close"
            className="w-9 h-9 rounded-full border border-charcoal-100 text-charcoal-500 hover:text-charcoal-700 hover:border-charcoal-400 flex items-center justify-center transition-colors shrink-0"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </header>

        {/* Body */}
        <div className="px-6 md:px-8 py-7 md:py-8 space-y-5">
          {isBlend ? (
            <>
              <div className="rounded-2xl border border-charcoal-100/60 bg-gradient-to-br from-[#f9efe6] via-cream-100 to-[#eef3e8] px-5 py-4">
                <p className="font-display text-[11px] tracking-[0.22em] font-bold uppercase text-charcoal-500 mb-1.5">
                  Proprietary Blend
                </p>
                <h3 className="font-display text-2xl font-bold text-charcoal-700 leading-tight">
                  What is {profile.name}?
                </h3>
                <p className="mt-2 text-sm text-charcoal-500 leading-relaxed">{profile.blendNote}</p>
              </div>
              {profile.components.map((c) => (
                <ProfilePanel key={c.name} profile={c} isComponent />
              ))}
            </>
          ) : (
            <ProfilePanel profile={profile} />
          )}

          {/* Footer note */}
          <p className="text-[11px] text-charcoal-400 leading-relaxed pt-2">
            Data references are provided for research-context verification only. LuviaRx supplies compounds for laboratory research use exclusively. Not for human consumption.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompoundProfileModal;
